import * as Tooltip from "@radix-ui/react-tooltip";
import classNames from "classnames/bind";
import { isEqual } from "es-toolkit/compat";
import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEventHandler,
} from "react";
import { ButtonWithPopover } from "../../../ButtonWithPopover";
import { colours } from "../../../constants/colours";
import { Icon } from "../../../Icon";
import { getLargestContentLength } from "../../../utils/dom";
import type { HeaderCellProps, ItemBaseType } from "../../types";
import styles from "./HeaderCell.module.scss";

const cx = classNames.bind(styles);

const HeaderCell = <ItemType extends ItemBaseType>({
  headerKey,
  label,
  tooltip,
  isLoading,
  alignment = "left",
  isFixed,
  isSticky,
  isDefaultFixed,
  style,
  isSortable,
  onClick,
  isLastFixedCell,
  sorting,
  headers,
  items,
  density,
  intent,
  colSpan,
  isGroup,
  shouldLeafCellsBreakMaxWidthCeiling,
  onLastFixedCellPositionChange,
  scrollLeftAffordancePosition,
  scrollRightAffordancePosition,
  handleScrollAffordanceClick,
}: HeaderCellProps<ItemType>) => {
  const cellRef = useRef<HTMLTableCellElement | undefined>();
  const [left, setLeft] = useState(0);
  const [shouldWrap, setShouldWrap] = useState<boolean>(false);
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);

  const handleClick = () => {
    if (isLoading) return;

    onClick?.();
  };

  const handleKeyDown: KeyboardEventHandler<HTMLTableCellElement> = (e) => {
    switch (e.key) {
      case "Enter":
        handleClick();
    }
  };

  const handleFocus = () => {
    setFocused(true);
  };

  const handleBlur = () => {
    setFocused(false);
  };

  const handleMouseOver = (setTo: boolean) => {
    if (focused) return;
    setHovered(setTo);
  };

  const handleMouseLeave: React.MouseEventHandler<HTMLTableCellElement> = (
    evt
  ) => {
    evt.currentTarget.blur();
    handleMouseOver(false);
  };

  const handleMouseEnter: React.MouseEventHandler<HTMLTableCellElement> = () =>
    handleMouseOver(true);

  const onRefChange = useCallback((node) => {
    cellRef.current = node;
  }, []);

  /**
   * position non-sticky headers
   */
  useEffect(() => {
    if (!cellRef.current) return;

    if (cellRef.current.parentNode) {
      const siblings = [
        ...cellRef.current.parentNode.childNodes,
      ] as HTMLElement[];

      // Don't run until painting has occurred
      requestAnimationFrame(() => {
        const newLeftPos = siblings.reduce(
          (total: number, node: HTMLElement, index: number) => {
            if (!cellRef.current) return total;

            if (
              index < siblings.indexOf(cellRef.current) &&
              getComputedStyle(node).position === "sticky"
            ) {
              return total + node.offsetWidth;
            }

            return total;
          },
          0
        );

        setLeft(newLeftPos);
        onLastFixedCellPositionChange?.(newLeftPos);
      });
    }
  }, [headers, items]);

  /**
   * check if the content overflows beyond the max width for cells
   */
  useLayoutEffect(() => {
    if (isLoading || !cellRef.current) return;

    requestAnimationFrame(() => {
      if (!cellRef.current) return;

      const largestWidth = getLargestContentLength(cellRef.current.firstChild);

      if (largestWidth > 380) {
        setShouldWrap(true);
      }
    });
  }, [isLoading, label]);

  const styleMemoized = useMemo<CSSProperties>(
    () => ({
      ...(style ?? {}),
      left,
    }),
    [style, left]
  );
  const scrollAffordanceStyleMemoized = useMemo<CSSProperties>(
    () => ({
      ...(scrollLeftAffordancePosition !== null
        ? { left: scrollLeftAffordancePosition }
        : {}),
      ...(scrollRightAffordancePosition !== null
        ? { right: scrollRightAffordancePosition }
        : {}),
    }),
    [scrollLeftAffordancePosition, scrollRightAffordancePosition]
  );

  const focusedOrHovered = focused || hovered;

  return (
    <th
      ref={onRefChange}
      className={cx(
        "cell",
        density && `cell--${density}`,
        intent && `cell--${intent}`,
        shouldWrap && "should-wrap",
        shouldWrap && "min-width-at-max-width",
        shouldLeafCellsBreakMaxWidthCeiling && "has-no-max-width",
        "cell--header",
        isSticky && "isSticky",
        isFixed && "sticky",
        isDefaultFixed && "sticky--default",
        isLastFixedCell && "sticky--last",
        isSortable && "sortable",
        isGroup && "isGroup",
        !label && "hole",
        sorting.sortBy === headerKey && "active"
      )}
      tabIndex={0}
      colSpan={colSpan}
      style={styleMemoized}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {!isLoading &&
      (scrollLeftAffordancePosition !== null ||
        scrollRightAffordancePosition !== null) ? (
        <div
          className={cx(
            "scroll-affordance",
            scrollLeftAffordancePosition !== null && "scroll-affordance--left",
            scrollRightAffordancePosition !== null && "scroll-affordance--right"
          )}
          tabIndex={0}
          style={scrollAffordanceStyleMemoized}
        >
          <ButtonWithPopover
            label="Pan to additional columns"
            buttonProps={{
              type: "button",
              className: cx("scroll-button"),
              onClick: (evt) => {
                evt.stopPropagation();
                evt.preventDefault();

                handleScrollAffordanceClick?.(
                  scrollLeftAffordancePosition !== null ? "left" : "right"
                );
              },
              icon:
                scrollLeftAffordancePosition !== null
                  ? "caret-left"
                  : "caret-right",
              iconProps: {
                size: 24,
              },
            }}
          ></ButtonWithPopover>
        </div>
      ) : null}
      <div className={cx("content", alignment === "right" && "reverse")}>
        <div className={cx("label-area")}>
          {tooltip ? (
            <Tooltip.Provider>
              <Tooltip.Root>
                <p className={cx("label")}>{label}</p>
                <Tooltip.Trigger className={cx("tooltip-trigger")}>
                  <Icon
                    variant="info"
                    size={16}
                    className={cx("tooltip-icon")}
                  />
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    className={cx("tooltip-content")}
                    sideOffset={5}
                    collisionPadding={16}
                  >
                    {tooltip}
                    <Tooltip.Arrow className={cx("tooltip-arrow")} />
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </Tooltip.Provider>
          ) : (
            <p className={cx("label")}>{label}</p>
          )}
        </div>
        <div className={cx("sorting-area")}>
          {isSortable &&
          ((sorting.sortBy === headerKey && sorting.sortDirection === "asc") ||
            (sorting.sortBy !== headerKey && focusedOrHovered)) ? (
            <Icon
              variant="sort-ascending"
              size={24}
              alt={
                sorting.sortBy === headerKey
                  ? "Sorted Ascending"
                  : "Sort Ascending"
              }
              {...(focusedOrHovered && sorting.sortBy !== headerKey
                ? { colour: colours.surface["300"] }
                : {})}
            />
          ) : isSortable &&
            sorting.sortBy === headerKey &&
            sorting.sortDirection === "desc" ? (
            <Icon
              variant="sort-descending"
              size={24}
              alt={
                sorting.sortBy === headerKey
                  ? "Sorted Descending"
                  : "Sort Descending"
              }
              {...(focusedOrHovered && sorting.sortBy !== headerKey
                ? { colour: colours.surface["300"] }
                : {})}
            />
          ) : null}
        </div>
      </div>
    </th>
  );
};

export default memo(HeaderCell, isEqual) as <ItemType extends ItemBaseType>(
  props: HeaderCellProps<ItemType>
) => JSX.Element;
